/**
 * Whether finance surfaces may be shown, and what the caller may do.
 *
 * Two layers, and the SERVER is the gate: the `financeExpenses` flag only decides whether we ask;
 * finance_my_access() decides the answer: `allowed` (sidebar link + /finance pages) and `event_access` (the event
 * Budget & Expenses tab). Owner rules 2026-10-27 (migration 1140): Finance, Admin and Superadmin get the Finance pages,
 * approvers of any role reach their queue, other event staff keep the event tab, and the MSR role sees and uses NO
 * finance anywhere -- the server refuses MSRs everywhere, and this list no longer asks for them (so the /finance route
 * guard sends them home too). Loading, disabled and error all mean "do not show".
 */
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { isFeatureEnabled } from "../utils/featureFlags";
import { normalizeRole } from "../utils/roleUtils";
import { getFinanceAccess } from "../services/finance.service";
import type { FinanceAccess } from "../types/finance.types";

/** Roles we ASK the server about (partners / end users / MSRs are never allowed). The server decides within these. */
export const FINANCE_ROLES = [
  "super_admin",
  "admin",
  "manager",
  "staff",
  "finance",
  "support_admin",
  "support_agent",
  "it",
  // "msr" removed 2026-10-27 (migration 1140): MSRs get no finance UI; the concierge team (Admin) enters expenses.
  "concierge",
];

export function useFinanceAccess(eventId?: string | null): {
  access: FinanceAccess | null;
  visible: boolean;
  loading: boolean;
} {
  const { user } = useAuth();
  const roleOk = FINANCE_ROLES.map(normalizeRole).includes(
    normalizeRole(user?.role),
  );
  const enabled = isFeatureEnabled("financeExpenses") && !!user?.id && roleOk;
  const q = useQuery({
    queryKey: ["finance-access", user?.id, eventId ?? null],
    queryFn: () => getFinanceAccess(eventId ?? null),
    enabled,
    staleTime: 60 * 1000,
  });
  const access = enabled ? (q.data ?? null) : null;
  return {
    // the event tab only needs event_access; the Finance pages need `visible`
    access,
    visible:
      !!access && access.enabled && (access.allowed ?? access.staff) === true,
    loading: enabled && q.isLoading,
  };
}
